import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { isFunction } from 'lodash-es';
import { CloseCircleFilledIcon as TdCloseCircleFilledIcon } from 'tdesign-icons-react';

import useConfig from '../hooks/useConfig';
import useControlled from '../hooks/useControlled';
import useDefaultProps from '../hooks/useDefaultProps';
import useDragSorter from '../hooks/useDragSorter';
import useGlobalIcon from '../hooks/useGlobalIcon';
import useUpdateLayoutEffect from '../hooks/useUpdateLayoutEffect';
import TInput from '../input';
import { tagInputDefaultProps } from './defaultProps';
import useHover from './useHover';
import useTagList from './useTagList';
import useTagScroll from './useTagScroll';

import type { CompositionEvent, KeyboardEvent, MouseEvent } from 'react';
import type { StyledProps } from '../common';
import type { InputRef, InputValue } from '../input';
import type { TdTagInputProps } from './type';

export interface TagInputProps extends TdTagInputProps, StyledProps {
  options?: any[]; // 参数穿透options, 给SelectInput/SelectInput 自定义选中项呈现的内容和多选状态下设置折叠项内容
}

const TagInput = forwardRef<InputRef, TagInputProps>((originalProps, ref) => {
  const props = useDefaultProps<TagInputProps>(originalProps, tagInputDefaultProps);
  const { classPrefix: prefix } = useConfig();
  const { CloseCircleFilledIcon } = useGlobalIcon({
    CloseCircleFilledIcon: TdCloseCircleFilledIcon,
  });

  const {
    excessTagsDisplayType,
    autoWidth,
    borderless,
    disabled,
    clearable,
    placeholder,
    valueDisplay,
    label,
    inputProps,
    size,
    tips,
    status,
    suffixIcon,
    suffix,
    prefixIcon,
    maxRows,
    onClick,
    onPaste,
    onFocus,
    onBlur,
  } = props;
  const readOnly = props.readOnly || props.readonly;
  const isBreakLine = excessTagsDisplayType === 'break-line';

  const [tInputValue, setTInputValue] = useControlled(props, 'inputValue', props.onInputChange);

  const { isHover, addHover, cancelHover } = useHover(props);
  const { getDragProps } = useDragSorter({
    ...props,
    sortOnDraggable: props.dragSort,
    onDragOverCheck: {
      x: true,
      targetClassNameRegExp: new RegExp(`^${prefix}-tag`),
    },
  });

  const suffixWidthRef = useRef<number>(0);
  const suffixIconWidthRef = useRef<number>(0);
  const isCompositionRef = useRef(false);

  const { scrollToRight, onWheel, scrollToRightOnEnter, scrollToLeftOnLeave, tagInputRef } = useTagScroll(props);

  // handle tag add and remove
  const { tagValue, onClose, onInnerEnter, onInputBackspaceKeyUp, clearAll, renderLabel, onInputBackspaceKeyDown } =
    useTagList({
      ...props,
      getDragProps,
    });

  useUpdateLayoutEffect(() => {
    if (excessTagsDisplayType === 'scroll') {
      scrollToRight();
    }
  }, [tagValue]);

  const NAME_CLASS = `${prefix}-tag-input`;
  const WITH_SUFFIX_ICON_CLASS = `${prefix}-tag-input__with-suffix-icon`;
  const CLEAR_CLASS = `${prefix}-tag-input__suffix-clear`;
  const BREAK_LINE_CLASS = `${prefix}-tag-input--break-line`;

  const tagInputPlaceholder = !tagValue?.length ? placeholder : '';

  const showClearIcon = Boolean(!readOnly && !disabled && clearable && isHover && (tagValue?.length || tInputValue));

  useImperativeHandle(ref as InputRef, () => ({
    ...(tagInputRef.current || {}),
  }));

  const updateSuffixWidth = (selector: string, cssVar: string, widthRef: React.MutableRefObject<number>) => {
    const wrapperEl = tagInputRef.current?.currentElement as HTMLElement;
    if (!wrapperEl) return;

    const inputEl = wrapperEl.querySelector(`.${prefix}-input`) as HTMLElement;
    if (!inputEl) return;

    const targetEl = wrapperEl.querySelector(selector);
    const width = targetEl ? targetEl.getBoundingClientRect().width : 0;
    if (width !== widthRef.current) {
      // eslint-disable-next-line no-param-reassign
      widthRef.current = width;
      if (width) {
        inputEl.style.setProperty(cssVar, `${Math.ceil(width + 8)}px`);
      } else {
        inputEl.style.removeProperty(cssVar);
      }
    }
  };

  useEffect(() => {
    if (!isBreakLine) return;

    if (suffix) {
      // 避免 suffix 左侧 与 tag 重合
      updateSuffixWidth(
        `.${prefix}-input__suffix:not(.${prefix}-input__suffix-icon)`,
        `--${prefix}-tag-input-suffix-width`,
        suffixWidthRef,
      );
    }

    // 确定 suffixIcon 右侧到 input 边框的距离
    updateSuffixWidth(`.${prefix}-input__suffix-icon`, `--${prefix}-tag-input-suffix-icon-width`, suffixIconWidthRef);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excessTagsDisplayType, suffix, suffixIcon, showClearIcon, prefix, tagInputRef, isBreakLine]);

  const onInputCompositionstart = (value: InputValue, context: { e: CompositionEvent<HTMLInputElement> }) => {
    isCompositionRef.current = true;
    inputProps?.onCompositionstart?.(value, context);
  };

  const onInputCompositionend = (value: InputValue, context: { e: CompositionEvent<HTMLInputElement> }) => {
    isCompositionRef.current = false;
    inputProps?.onCompositionend?.(value, context);
  };

  const onInputEnter = (value: InputValue, context: { e: KeyboardEvent<HTMLInputElement> }) => {
    context.e?.preventDefault?.();
    setTInputValue('', { e: context.e, trigger: 'enter' });
    if (!isCompositionRef.current) {
      onInnerEnter(value, context);
    }
  };

  const onInnerClick = (context: { e: MouseEvent<HTMLDivElement> }) => {
    if (!props.disabled && !readOnly) {
      (tagInputRef.current as any)?.inputElement?.focus?.();
    }
    onClick?.(context);
  };

  const onClearClick = (e: MouseEvent<SVGSVGElement>) => {
    clearAll({ e });
    setTInputValue('', { e, trigger: 'clear' });
    props.onClear?.({ e });
  };

  const onKeydown = (value: string, context: { e: React.KeyboardEvent<HTMLInputElement> }) => {
    onInputBackspaceKeyDown(value, context);
    inputProps?.onKeydown?.(value, context);
  };

  const onKeyup = (value: string, context: { e: React.KeyboardEvent<HTMLInputElement> }) => {
    onInputBackspaceKeyUp(value);
    inputProps?.onKeyup?.(value, context);
  };

  const suffixIconNode = showClearIcon ? (
    <CloseCircleFilledIcon className={CLEAR_CLASS} onClick={onClearClick} />
  ) : (
    suffixIcon
  );

  // 自定义 Tag 节点
  const displayNode = useMemo(
    () =>
      isFunction(valueDisplay)
        ? valueDisplay({
            value: tagValue,
            onClose: (index) => onClose({ index }),
          })
        : valueDisplay,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueDisplay],
  );

  const isEmpty = !(Array.isArray(tagValue) && tagValue.length);

  const classes = [
    NAME_CLASS,
    {
      [BREAK_LINE_CLASS]: isBreakLine,
      [WITH_SUFFIX_ICON_CLASS]: !!suffixIconNode,
      [`${prefix}-is-empty`]: isEmpty,
      [`${prefix}-tag-input--with-tag`]: !isEmpty,
      [`${prefix}-tag-input--max-rows`]: isBreakLine && maxRows,
      [`${prefix}-tag-input--drag-sort`]: props.dragSort && !disabled && !readOnly,
    },
    props.className,
  ];

  const maxRowsStyle = maxRows
    ? ({
        '--max-rows': maxRows,
        '--tag-input-size': size,
      } as React.CSSProperties)
    : {};

  return (
    <TInput
      ref={tagInputRef as React.RefObject<InputRef>}
      value={tInputValue}
      onChange={(val, context) => {
        setTInputValue(val, { ...context, trigger: 'input' });
      }}
      autoWidth={true} // 控制input_inner的宽度 设置为true让内部input不会提前换行
      onWheel={onWheel}
      size={size}
      borderless={borderless}
      readOnly={readOnly}
      disabled={disabled}
      label={renderLabel({ displayNode, label })}
      style={{
        ...props.style,
        ...maxRowsStyle,
      }}
      tips={tips}
      status={status}
      placeholder={tagInputPlaceholder}
      suffix={suffix}
      prefixIcon={prefixIcon}
      suffixIcon={suffixIconNode}
      showInput={!inputProps?.readOnly || !inputProps?.readonly || !tagValue || !tagValue?.length}
      keepWrapperWidth={!autoWidth}
      onPaste={onPaste}
      onClick={onInnerClick}
      onEnter={onInputEnter}
      onMouseenter={(context) => {
        addHover(context);
        scrollToRightOnEnter();
      }}
      onMouseleave={(context) => {
        cancelHover(context);
        scrollToLeftOnLeave();
      }}
      onFocus={(inputValue, context) => {
        onFocus?.(tagValue, { e: context.e, inputValue });
      }}
      onBlur={(inputValue, context) => {
        if (tInputValue) {
          setTInputValue('', { e: context.e, trigger: 'blur' });
        }
        onBlur?.(tagValue, { e: context.e, inputValue });
      }}
      {...inputProps}
      onCompositionstart={onInputCompositionstart}
      onCompositionend={onInputCompositionend}
      onKeydown={onKeydown}
      onKeyup={onKeyup}
      className={classnames(classes, inputProps?.className)}
    />
  );
});

TagInput.displayName = 'TagInput';

export default TagInput;
